import { useNavigate, useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Loader, toast } from "@/components/kit";
import { ProductFormPageHeader } from "@/modules/products/presentation/ui/ProductFormPageHeader";
import { formatDateForInput } from "@/shared/lib/date-time";
import {
  useImportOrder,
  useUpdateImportOrder,
} from "../../api/import-orders-queries";
import { consumeImportOrderPhase13DraftForReturnTo } from "../../lib/import-prefill-phase13";
import { importLineDtoToFormLine } from "../../lib/import-order-line-form";
import { ImportOrderPhase13Form } from "../../ui/ImportOrderPhase13Form";

export function ImportOrderEditPage() {
  const nav = useNavigate();
  const { id } = useParams({ from: "/app/import-orders/$id/edit" });
  const order = useImportOrder(id);
  const updateOrder = useUpdateImportOrder(id);

  const draftDefaults = useMemo(
    () =>
      consumeImportOrderPhase13DraftForReturnTo(
        `/app/import-orders/${id}/edit`,
      ),
    [id],
  );

  const defaultValues = useMemo(() => {
    if (draftDefaults) return draftDefaults;

    const data = order.data;
    if (!data) return undefined;
    return {
      externalOrderNo: data.externalOrderNo,
      source: data.source,
      currency: (data.currency === "LAK" ? "LAK" : "CNY") as "CNY" | "LAK",
      exchangeRate: Number(data.exchangeRate),
      shippingTotalLak: Number(data.shippingTotalLak),
      orderedAt: data.orderedAt
        ? formatDateForInput(new Date(data.orderedAt))
        : "",
      expectedArrivalDate: data.expectedArrivalDate
        ? formatDateForInput(new Date(data.expectedArrivalDate))
        : "",
      note: data.note ?? "",
      trackingNo: data.trackings[0]?.trackingNo ?? "",
      carrierName: data.trackings[0]?.carrierName ?? "",
      lines: data.lines
        .filter((l) => l.matchStatus !== "skipped")
        .map(importLineDtoToFormLine),
    };
  }, [draftDefaults, order.data]);

  if (order.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex justify-center py-16">
          <Loader />
        </Main>
      </>
    );
  }

  if (!order.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="brand-card p-8 text-center text-muted-foreground text-sm">
            ບໍ່ພົບໃບສັ່ງນຳເຂົ້າ
          </div>
        </Main>
      </>
    );
  }

  // lock add/remove lines only after receive or short recorded
  const readOnlyLineStructure = order.data.lines.some(
    (line) => line.receivedQuantity > 0 || line.shortQuantity > 0,
  );
  const orderData = order.data;

  return (
    <>
      <Header />
      <Main>
        <ProductFormPageHeader
          title={`ແກ້ໄຂ ${order.data.externalOrderNo}`}
          subtitle={
            readOnlyLineStructure
              ? "ແກ້ໄຂຈຳນວນ, ລາຄາ, ຄ່າຂົນສົ່ງ ແລະ ຫົວໃບ — ເພີ່ມ/ລຶບລາຍການຖືກລັອກເພາະມີການຮັບເຂົ້າແລ້ວ"
              : "ແກ້ໄຂຂໍ້ມູນ ແລະ ລາຍການສິນຄ້າໄດ້"
          }
          onBack={() =>
            nav({ to: "/app/import-orders/$id", params: { id } })
          }
        />
        <ImportOrderPhase13Form
          defaultValues={defaultValues}
          productCreateReturnTo={`/app/import-orders/${id}/edit`}
          readOnlyLineStructure={readOnlyLineStructure}
          submitting={updateOrder.isPending}
          submitLabel={updateOrder.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການແກ້ໄຂ"}
          onSubmit={async (input, formLines) => {
            try {
              const linePrices = readOnlyLineStructure
                ? orderData.lines.map((existing) => {
                    const edited = formLines.find(
                      (line) => line.lineKey === existing.id,
                    );
                    return {
                      importOrderLineId: existing.id,
                      unitPriceForeign:
                        edited?.unitPriceForeign ??
                        Number(existing.unitPriceForeign),
                    };
                  })
                : undefined;

              const lineQuantities = readOnlyLineStructure
                ? orderData.lines.map((existing) => {
                    const edited = formLines.find(
                      (line) => line.lineKey === existing.id,
                    );
                    return {
                      importOrderLineId: existing.id,
                      orderedQuantity:
                        edited?.orderedQuantity ?? existing.orderedQuantity,
                    };
                  })
                : undefined;

              const newTracking = input.trackings?.[0];
              const nextTrackings =
                newTracking?.trackingNo &&
                !orderData.trackings.some(
                  (tracking) => tracking.trackingNo === newTracking.trackingNo,
                )
                  ? input.trackings
                  : undefined;

              await updateOrder.mutateAsync({
                externalOrderNo: input.externalOrderNo,
                source: input.source,
                currency: input.currency,
                exchangeRate: input.exchangeRate,
                shippingTotalLak: input.shippingTotalLak,
                orderedAt: input.orderedAt,
                expectedArrivalDate: input.expectedArrivalDate,
                note: input.note,
                lines: readOnlyLineStructure ? undefined : input.lines,
                linePrices,
                lineQuantities,
                trackings: nextTrackings,
              });
              toast.success("ອັບເດດແລ້ວ");
              nav({ to: "/app/import-orders/$id", params: { id } });
            } catch (e) {
              toast.error("ອັບເດດລົ້ມເຫຼວ", {
                description: e instanceof Error ? e.message : undefined,
              });
            }
          }}
        />
      </Main>
    </>
  );
}
